import { useMemo } from "react";
import { Drawer, Tooltip, message } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../utils/ErrorUtils";
import { buildUserManagerUrl } from "../../../utils/NavigationUtils";
import useGetUserTransactionDetails from "../../../queries/user-manager/useGetUserTransactionDetails";
import type {
  TransactionDetailField,
  TransactionDetailSection,
  TransactionDetailStatus,
  TransactionDetailsData,
} from "../../../types/transactions";
import RebetLoader from "../../CommonComponents/RebetLoader/RebetLoader";
import { formatCurrency } from "../../../utils/financialFormatters";
import styles from "./PaymentDetailsDrawer.module.css";

export type PaymentDetailsDrawerProps = {
  open: boolean;
  onClose: () => void;
  userId?: string | null;
  transactionId?: string | null;
  currencySymbol?: string;
  /** Defaults to the V1 details route; only the Report Generator passes V2. */
  detailsRoute?: string;
};

type StatusTone = "successful" | "pending" | "declined" | "refunded";
const USER_DETAILS_FIELD_KEYS = new Set(["username", "user_id", "email"]);

export function getPaymentStatusTone(
  status: TransactionDetailStatus | string | null | undefined,
): StatusTone | null {
  if (status == null || status === "") return null;
  const normalized = String(status).trim().toLowerCase();
  if (
    normalized === "approved/initiated" ||
    normalized === "successful" ||
    normalized === "approved" ||
    normalized === "initiated"
  ) {
    return "successful";
  }
  if (normalized === "pending") return "pending";
  if (normalized === "declined") return "declined";
  if (normalized === "refunded") return "refunded";
  return null;
}

export function getPaymentStatusLabel(
  status: TransactionDetailStatus | string | null | undefined,
): string {
  if (status == null || status === "") return "";
  const tone = getPaymentStatusTone(status);
  const STATUS_LABELS: Record<StatusTone, string> = {
    successful: AppStrings.PAYMENT_STATUS_SUCCESSFUL,
    pending: AppStrings.STATUS_PENDING,
    declined: AppStrings.STATUS_DECLINED,
    refunded: AppStrings.PAYMENT_STATUS_REFUNDED,
  };
  return tone ? STATUS_LABELS[tone] : String(status);
}

export function formatPaymentAmountLabel(
  details: Pick<
    TransactionDetailsData,
    "currency" | "currency_symbol" | "transaction"
  > | null | undefined,
  currencySymbol?: string,
): string {
  if (!details?.transaction) return "";
  const rawAmount = details.transaction.amount;
  if (rawAmount == null || rawAmount === "") return "";

  const numeric = Number(rawAmount);
  const symbol =
    details.currency_symbol?.trim() || currencySymbol || "";
  const currency = details.currency?.trim() || "";
  const money = Number.isFinite(numeric)
    ? formatCurrency(numeric, symbol, 2)
    : `${symbol}${String(rawAmount)}`;

  return currency ? `${money} ${currency}` : money;
}

function findFieldValue(
  sections: TransactionDetailSection[] | undefined,
  key: string,
): string | undefined {
  if (!sections) return undefined;
  for (const section of sections) {
    const field = section.fields?.find((f) => f.key === key);
    if (field?.value) return field.value;
  }
  return undefined;
}

function isUserDetailsSection(section: TransactionDetailSection): boolean {
  return section.key === "user_details";
}

export function isSafeHttpUrl(value: string | null | undefined): boolean {
  return /^https?:\/\//i.test(value ?? "");
}

function DetailRow({
  field,
  usernameHref,
}: {
  field: TransactionDetailField;
  usernameHref?: string;
}) {
  // Only render as an anchor when the BE marks the field as a link AND the
  // value is an http(s) URL — blocks javascript:/data: XSS if a payload is
  // ever injected into the details response.
  const isBackendLink =
    field.type === "link" &&
    Boolean(field.value) &&
    isSafeHttpUrl(field.value);
  const href = usernameHref ?? (isBackendLink ? field.value : undefined);
  const isPill = field.key === "user_id";
  const infoTooltip =
    field.key === "date_added"
      ? AppStrings.PAYMENT_DATE_ADDED_TOOLTIP
      : undefined;

  return (
    <div className={styles.row} data-testid={`payment-details-field-${field.key}`}>
      <span className={styles.label}>
        {field.label}
        {infoTooltip ? (
          <Tooltip title={infoTooltip}>
            <InfoCircleOutlined
              className={styles.infoIcon}
              data-testid="payment-details-info-icon"
            />
          </Tooltip>
        ) : null}
      </span>
      {href ? (
        <a
          className={`${styles.value} ${styles.linkValue}`}
          href={href}
          target="_blank"
          rel="noreferrer"
        >
          {field.value}
        </a>
      ) : (
        <span className={`${styles.value}${isPill ? ` ${styles.pill}` : ""}`}>
          {field.value || "—"}
        </span>
      )}
    </div>
  );
}

function PaymentDetailsDrawer({
  open,
  onClose,
  userId,
  transactionId,
  currencySymbol,
  detailsRoute,
}: PaymentDetailsDrawerProps) {
  const { data, isLoading, isError, error } = useGetUserTransactionDetails({
    userId,
    transactionId,
    enabled: open,
    detailsRoute,
  });

  const amountLabel = useMemo(
    () => formatPaymentAmountLabel(data, currencySymbol),
    [currencySymbol, data],
  );
  const visibleSections = useMemo(
    () =>
      (data?.sections ?? [])
        .map((section) => ({
          ...section,
          fields: (section.fields ?? []).filter(
            (field) =>
              field.key !== "transaction_id" &&
              (isUserDetailsSection(section) ||
                !USER_DETAILS_FIELD_KEYS.has(field.key)),
          ),
        }))
        .filter((section) => section.fields.length > 0),
    [data?.sections],
  );
  const detailsUserId =
    findFieldValue(data?.sections, "user_id") || userId || undefined;
  const userOverviewUrl = detailsUserId
    ? buildUserManagerUrl(detailsUserId)
    : undefined;
  const detailsTransactionId =
    data?.transaction?.transaction_id || transactionId || undefined;
  const statusTone = getPaymentStatusTone(data?.status);
  const statusLabel = getPaymentStatusLabel(data?.status);

  const statusClass =
    statusTone === "successful"
      ? styles.statusSuccessful
      : statusTone === "pending"
        ? styles.statusPending
        : statusTone === "declined"
          ? styles.statusDeclined
          : statusTone === "refunded"
            ? styles.statusRefunded
          : "";

  const errorMessage = isError
    ? getApiErrorMessage(error, AppStrings.PAM_GENERIC_REQUEST_FAILED)
    : "";

  const handleCopy = async () => {
    if (!detailsTransactionId) return;

    try {
      await navigator.clipboard.writeText(String(detailsTransactionId));
      message.success(AppStrings.PAYMENT_COPY_SUCCESS);
    } catch {
      message.error(AppStrings.NEW_AFFILIATE_CONFIRMATION_COPY_FAILED);
    }
  };

  return (
    <Drawer
      className={styles.drawer}
      title={AppStrings.PAYMENT_DETAILS}
      placement="right"
      width={500}
      open={open}
      onClose={onClose}
      destroyOnHidden
      data-testid="payment-details-drawer"
    >
      <div className={styles.content}>
        {isLoading ? (
          <div
            className={styles.loadingContainer}
            data-testid="payment-details-loading"
          >
            <RebetLoader height="80px" width="80px" />
          </div>
        ) : null}

        {isError ? (
          <p className={styles.errorMessage} data-testid="payment-details-error">
            {errorMessage}
          </p>
        ) : null}

        {!isLoading && !isError && data ? (
          <>
            <div className={styles.summaryHeader}>
              {data.direction ? (
                <h3 className={styles.summaryTitle}>{data.direction}</h3>
              ) : (
                <span />
              )}
              <Tooltip title={String(detailsTransactionId ?? "")}>
                <button
                  type="button"
                  className={styles.copyButton}
                  onClick={handleCopy}
                  data-testid="payment-details-copy"
                >
                  {AppStrings.ID}
                </button>
              </Tooltip>
            </div>

            <div className={styles.amountRow}>
              {amountLabel ? (
                <p className={styles.amount}>{amountLabel}</p>
              ) : null}
              {data.status ? (
                <span
                  className={`${styles.statusBadge}${statusClass ? ` ${statusClass}` : ""}`}
                  data-testid="payment-details-status"
                >
                  {statusLabel || data.status}
                </span>
              ) : null}
            </div>

            {visibleSections.map((section) => (
              <section
                key={section.key}
                className={styles.section}
                data-testid={`payment-details-section-${section.key}`}
              >
                <h4 className={styles.sectionTitle}>{section.title}</h4>
                {(section.fields ?? []).map((field) => (
                  <DetailRow
                    key={field.key}
                    field={field}
                    usernameHref={
                      isUserDetailsSection(section) &&
                      field.key === "username"
                        ? userOverviewUrl
                        : undefined
                    }
                  />
                ))}
              </section>
            ))}
          </>
        ) : null}
      </div>
    </Drawer>
  );
}

export default PaymentDetailsDrawer;
